import { Injectable, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PaymentService } from '../services/payment.service';
import { ToastService } from '../../../shared/services/toast.service';
import { globalMessages } from '../../../shared/global-message';

declare var OneInc: any;

export interface OneIncSaveRequest {
  quoteId: string;
  customerId: string;
  sessionKey?: string;
  paymentCategory: string;
  billingZip: string;
  billingAddressStreet: string;
  policyHolderName: string;
}

export interface OneIncCallbacks {
  onSuccess: (result: any) => void;
  onFail: () => void;
  onValidationComplete: (ready: boolean) => void;
}

@Injectable()
export class OneIncService {
  private readonly paymentService = inject(PaymentService);
  private readonly toastService = inject(ToastService);

  private sessionID: string | null = null;
  private portalOne: any = null;
  private proxy: any = null;

  /**
   * Opens a OneInc save-payment session and renders the iframe in the given container.
   */
  openSession(
    quoteId: string,
    destroy$: Subject<void>,
    onSessionReady: (sessionKey: string) => void,
    onSessionFailed: () => void
  ): void {
    this.hideModal();
    const request = {
      quoteid: quoteId,
      action: globalMessages.actionSession,
      targetOperation: globalMessages.targetOperationSavePaymentMethod,
    };
    this.paymentService
      .getPaymentSession(request)
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (response: any) => {
          this.sessionID = response['portalOneSessionKey'];
          if (!this.sessionID) {
            onSessionFailed();
            return;
          }
          onSessionReady(this.sessionID);
        },
        error: () => {
          onSessionFailed();
        },
      });
  }

  /**
   * Initialize the PortalOne widget and open the save payment method window.
   */
  openWindow(
    containerId: string,
    saveRequest: OneIncSaveRequest,
    callbacks: OneIncCallbacks
  ): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    this.portalOne = new OneInc.PortalOne(
      container,
      (el: HTMLElement) => {
        if (el) el.addEventListener('portalOne.load', () => {});
      },
      null
    );

    const isCreditCard = saveRequest.paymentCategory === OneInc.PaymentCategory.CreditCard;

    const request = {
      paymentCategory: saveRequest.paymentCategory,
      feeContext: globalMessages.oneIncFeeContext,
      billingZip: saveRequest.billingZip,
      billingAddressStreet: saveRequest.billingAddressStreet,
      policyHolderName: saveRequest.policyHolderName,
      referenceNumber: saveRequest.quoteId,
      customerId: saveRequest.customerId,
      sessionId: saveRequest.sessionKey,
      displayMode: globalMessages.oneIncDisplayModeInline,
      interactionMode: globalMessages.oneIncInteractionMode,
    };

    this.proxy = this.portalOne.savePaymentMethod(request, {
      onSuccess: callbacks.onSuccess,
      onFail: callbacks.onFail,
      onValidationComplete: callbacks.onValidationComplete,
    });
  }

  /**
   * Trigger the save action on the current proxy.
   */
  save(): void {
    this.proxy?.Save();
  }

  /**
   * Whether a proxy is active and ready to save.
   */
  hasProxy(): boolean {
    return !!this.proxy;
  }

  /**
   * Destroy the OneInc modal and clear state.
   */
  hideModal(): void {
    this.sessionID = null;
    this.portalOne = null;
    this.proxy = null;
    const ele = document.getElementById(globalMessages.oneIncContainerId);
    while (ele?.firstChild) ele.removeChild(ele.firstChild);
  }
}
